import React, { ReactElement, useMemo, useState } from 'react';
import { Text } from 'rebass/styled-components';

import { Card, CardHeader, Dropdown } from 'ui/patterns';
import {
  StickyListWrap,
  StickyList,
  StickyListItem,
  Button,
  Badge,
} from 'ui/components';

import * as Icon from 'assets/icons';

import { SelectedProceduresListType } from './selectedProcedureListTypes';
import { showErrorToast, showSuccessToast } from 'utils/showToast';
import { Tooltip } from 'react-tippy';
import { maxProceduresForCategoryChange } from 'utils/constants/Procedure/procedureConstants';

export default function SelectedProceduresList({
  selectedProcedures,
  setSelectedProcedures,
  procedureList,
  categoryMenuList,
  listItemDropdown,
  updateProcedureCategory,
  isProcedureCategoryUpdateInProgress,
}: SelectedProceduresListType): ReactElement {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  const removeProceduresfromSelectionList = (id: number) => {
    const selectedProc = [...selectedProcedures];
    const index =
      selectedProc && selectedProc.length > 0
        ? selectedProc.findIndex(item => item === id)
        : -1;
    if (index >= 0) {
      selectedProc && selectedProc.splice(index, 1);
    }
    setSelectedProcedures([...selectedProc]);
  };

  const policyMenuSelected = useMemo(() => {
    return selectedProcedures?.some(id => {
      const proc = procedureList?.find(p => p.ProcedureId === id);
      return proc && proc.ProcedureName === '0000_POLICIESMENU.html';
    });
  }, [selectedProcedures, procedureList]);

  return (
    <Card
      flex="1"
      ml={3}
      position="relative"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <CardHeader p={3} bg="var(--color-neutral-contrast-02)"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
        <Text as="h4" fontWeight="medium" lineHeight={1} color="text.body">
          {`${selectedProcedures?.length} procedures selected`}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {!policyMenuSelected
            ? (
              <>
                <Tooltip
                  title={
                    selectedProcedures?.length > maxProceduresForCategoryChange
                      ? `Please limit your selection to ${maxProceduresForCategoryChange} procedures to change the category.`
                      : ''
                  }
                  position="top"
                  arrow={true}
                >
                  <Dropdown
                    menuList={categoryMenuList}
                    //disabled={globalOrLinkedSelected}
                    disabled={selectedProcedures?.length > maxProceduresForCategoryChange || isProcedureCategoryUpdateInProgress}
                    trigger={listItemDropdown ? listItemDropdown(categoryMenuList) : null}
                    position="right"
                    categoryMenu={true}
                    onButtonClick={categoryId => {
                      if (categoryId !== null && categoryId !== undefined) {
                        setSelectedCategoryId(categoryId);

                        updateProcedureCategory(
                          {
                            accountId: procedureList?.[0]?.AccountId,
                            procedureCategoryId: categoryId,
                            procedureIds: selectedProcedures || [],
                          },
                          {
                            onSuccess: () => {
                              showSuccessToast({
                                message: 'Procedure Category was updated successfully.',
                              });
                            },
                            onError: (error: any) => {
                              showErrorToast({
                                message: error?.Errors[0].Message,
                              });
                            },
                          }
                        );
                      }
                    }}
                  />
                </Tooltip>
                {isProcedureCategoryUpdateInProgress && <Badge variant='warning' style={{ alignSelf: 'center', alignItems: 'center', display: 'flex', gap: '5px' }}><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="rotating" style={{ width: '16px', height: '16px' }}>
                  <path fillRule="evenodd" d="M13.836 2.477a.75.75 0 0 1 .75.75v3.182a.75.75 0 0 1-.75.75h-3.182a.75.75 0 0 1 0-1.5h1.37l-.84-.841a4.5 4.5 0 0 0-7.08.932.75.75 0 0 1-1.3-.75 6 6 0 0 1 9.44-1.242l.842.84V3.227a.75.75 0 0 1 .75-.75Zm-.911 7.5A.75.75 0 0 1 13.199 11a6 6 0 0 1-9.44 1.241l-.84-.84v1.371a.75.75 0 0 1-1.5 0V9.591a.75.75 0 0 1 .75-.75H5.35a.75.75 0 0 1 0 1.5H3.98l.841.841a4.5 4.5 0 0 0 7.08-.932.75.75 0 0 1 1.025-.273Z" clipRule="evenodd" />
                </svg>
                  Category updation is in progress.</Badge>}
              </>
            ) : null}
        </div>
      </CardHeader>

      <StickyListWrap height="1px" overflowY="auto" flex="1 0 auto">
        <StickyList>
          {selectedProcedures &&
            selectedProcedures.length >= 0 &&
            selectedProcedures?.map((item, index) => {
              const selectedProcedureItem = procedureList?.find(
                procedureListItem => procedureListItem.ProcedureId === item,
              );
              return (
                <StickyListItem key={index} tabIndex={index}>
                  {selectedProcedureItem?.ProcedureName}
                  <Button
                    variant="subtle"
                    size="sm"
                    title="Remove procedure"
                    onClick={() => removeProceduresfromSelectionList(item)}
                  >
                    <Icon.X title="Remove procedure" />
                  </Button>
                </StickyListItem>
              );
            })}
        </StickyList>
      </StickyListWrap>
    </Card>
  );
}
